// Admin › Console: which client's console a principal is looking at.
//
// WHY THIS EXISTS. SeaState's two principals administer every client and belong
// to none of them. Their accounts sit under the SeaState row, and the console
// they see is chosen here: pick a client, and the console reloads as that
// client's — the fleet console for TDI-Brooks, the thermal console for PG&E —
// reading that client's data and nothing else's. Cody, 2026-10-06: "There
// should be two admin accounts, one for Les and one for me. These are not
// related to the accounts that either PG&E or TDI Brooks has."
//
// WHAT IT IS NOT. Not "act as that client": the session, the audit trail and
// the staff flag stay the principal's, and writing a client's spec still asks
// for THAT client's operations password (server/opsgate.go). The server decides
// everything (server/tenant.go: effectiveClient honours the stored override only
// when users.staff is true); this page only asks it to store a choice.
//
// The client's own people never see this page, and the column it writes is
// inert on their rows no matter what is in it.

interface ViewClient {
  id: number;
  name: string;
  key: string;
  console: string;
  status: string;
}

interface ViewState {
  clients: ViewClient[];
  /** The override, or null when the principal is on their own row. */
  viewing: number | null;
  own: number;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Narrow the /api/admin/viewing envelope; null when it is not one. */
export function toViewState(raw: unknown): ViewState | null {
  if (!isRecord(raw) || !Array.isArray(raw.clients)) return null;
  const own = num(raw.own);
  if (own === null) return null;
  const clients: ViewClient[] = [];
  for (const c of raw.clients) {
    if (!isRecord(c)) continue;
    const id = num(c.id);
    if (id === null) continue;
    clients.push({ id, name: str(c.name), key: str(c.key), console: str(c.console), status: str(c.status) });
  }
  const viewing = num(raw.viewing);
  return { clients, viewing, own };
}

/** The id whose console the principal currently sees. */
export function currentClient(s: ViewState): number {
  return s.viewing ?? s.own;
}

const CONSOLE_WORD: Record<string, string> = {
  fleet: "fleet console — vessels, operations, workability",
  thermal: "thermal console — intake temperature against the threshold",
};

export function mountAdminViewing(host: HTMLElement): void {
  const card = el("section", "ad-card");
  card.appendChild(el("h2", undefined, "Console"));
  card.appendChild(
    el(
      "p",
      "ad-para",
      "Pick the client whose console you want to see. The console reloads as that client's, " +
        "reading only that client's data. Your sign-in, your name on the audit trail and your " +
        "admin access stay yours; changing a client's own settings still asks for that client's " +
        "operations password.",
    ),
  );
  const status = el("p", "ad-status");
  const list = el("div", "ad-viewing");
  card.append(list, status);
  host.appendChild(card);

  const setStatus = (text: string, kind: "" | "bad"): void => {
    status.textContent = text;
    status.className = "ad-status" + (kind ? " " + kind : "");
  };

  const paint = (s: ViewState): void => {
    list.replaceChildren();
    const now = currentClient(s);
    for (const c of s.clients) {
      const row = el("button", "ad-viewing-row" + (c.id === now ? " on" : ""));
      row.type = "button";
      const head = el("span", "ad-viewing-name", c.name);
      const meta = el(
        "span",
        "ad-viewing-meta",
        (CONSOLE_WORD[c.console] ?? `${c.console} console`) +
          (c.status === "development" ? " · development" : "") +
          (c.id === s.own ? " · your own row" : ""),
      );
      row.append(head, meta);
      if (c.id === now) {
        row.appendChild(el("span", "ad-viewing-now", "viewing now"));
        row.disabled = true;
      } else {
        row.addEventListener("click", () => void choose(c));
      }
      list.appendChild(row);
    }
  };

  const choose = async (c: ViewClient): Promise<void> => {
    setStatus(`Switching to ${c.name}…`, "");
    try {
      const res = await fetch("/api/admin/viewing", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: c.id }),
      });
      const payload: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = isRecord(payload) ? str(payload.error) : "";
        setStatus(msg !== "" ? msg : `Could not switch (HTTP ${res.status}).`, "bad");
        return;
      }
      // A whole console is being swapped — its kind, its data, its branding and
      // half its state in the URL — so a full load of the console is the honest
      // way to show it, not a repaint.
      window.location.assign("/console");
    } catch {
      setStatus("Could not reach the server.", "bad");
    }
  };

  const load = async (): Promise<void> => {
    list.replaceChildren(el("div", "ad-empty", "Loading clients…"));
    try {
      const res = await fetch("/api/admin/viewing", { credentials: "same-origin" });
      const payload: unknown = await res.json().catch(() => null);
      const s = res.ok ? toViewState(payload) : null;
      if (s === null) {
        const msg = isRecord(payload) ? str(payload.error) : "";
        list.replaceChildren(
          el("div", "ad-empty", msg !== "" ? msg : `Could not load the client list (HTTP ${res.status}).`),
        );
        return;
      }
      paint(s);
    } catch {
      list.replaceChildren(el("div", "ad-empty", "Could not reach the server."));
    }
  };
  void load();
}
